import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faTimes } from '@fortawesome/free-solid-svg-icons';
import { useTranslation } from 'react-i18next';
import { useEffect, useState } from 'react';
import { useForm } from 'react-hook-form';
import { locations } from '@modules/organizationalStructure/locations/services/locations';
import useFormHook from '@shared/hooks/useFormHook';
import { notifySuccess } from '@config/tostifyConfig/tostifyConfig';

export default function LocationTranslations({ onClose, location, onSuccess }) {
    const { t } = useTranslation();
    const { modalRef } = useFormHook({}, onClose);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const { register, handleSubmit, setValue } = useForm();

    useEffect(() => {
        const fetchTranslations = async () => {
            if (location) {
                try {
                    setLoading(true);
                    setError(null);
                    const translationData = await locations.getTranslation(location.id);
                    
                    // Set original values
                    setValue('name', location.name);
                    setValue('description', location.description);
                    setValue('address1', location.address1);
                    setValue('address2', location.address2);
                    setValue('address3', location.address3);

                    // Set translated values if they exist
                    if (translationData && translationData.length > 0) {
                        const translation = translationData[0]; // Get first translation
                        setValue('nameTranslated', translation.name || '');
                        setValue('descriptionTranslated', translation.description || '');
                        setValue('address1Translated', translation.address1 || '');
                        setValue('address2Translated', translation.address2 || '');
                        setValue('address3Translated', translation.address3 || '');
                    } else {
                        // If no translations exist, set empty values
                        setValue('nameTranslated', '');
                        setValue('descriptionTranslated', '');
                        setValue('address1Translated', '');
                        setValue('address2Translated', '');
                        setValue('address3Translated', '');
                    }
                } catch (err) {
                    console.error('Error fetching translations:', err);
                    setError('Failed to load translations');
                } finally {
                    setLoading(false);
                }
            }
        };

        fetchTranslations();
    }, [location, setValue]);

    const submitForm = async (data) => {
        try {
            const translationData = {
                locationId: location.id,
                langCode: "en_US",
                name: data.nameTranslated,
                description: data.descriptionTranslated,
                address1: data.address1Translated,
                address2: data.address2Translated,
                address3: data.address3Translated
            };

            await locations.saveTranslation(translationData);
            onClose();
            notifySuccess(t('common.translationSuccessfully'));
            if (onSuccess) {
                onSuccess();
            }
        } catch (error) {
            console.error('Error saving translation:', error);
            setError('Failed to save translation');
        }
    };

    if (loading) {
        return (
            <div className="modal show" tabIndex="-1">
                <div className="modal-dialog modal-confirm" ref={modalRef}>
                    <div className="modal-content">
                        <div className="modal-body">
                            <p>Loading translations...</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    return (
        <div className="modal show" tabIndex="-1">
            <div className="modal-dialog modal-confirm" ref={modalRef}>
                <div className="modal-content">
                    <button type="button" className="close" onClick={onClose}>
                        <FontAwesomeIcon icon={faTimes} />
                    </button>
                    <div className="modal-body">
                        <h4 className="modal-title">
                            {t('organizationalStructure.locations.form.translations')}
                        </h4>
                        <form id="translationsForm" className="region-form" onSubmit={handleSubmit(submitForm)}>
                            <div className="form-sections">
                                <div className="form-section width-80">
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.table.headers.abbreviation')}</span>
                                        <input
                                            type="text"
                                            className="small-input"
                                            {...register('description', { required: true })}
                                            disabled
                                        />
                                    </div>

                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.table.headers.title')}</span>
                                        <input
                                            type="text"
                                            className="small-input"
                                            {...register('name', { required: true })}
                                            disabled
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.street')}</span>
                                        <input
                                            type="text"
                                            className="small-input"
                                            {...register('address1')}
                                            disabled
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.streetNo')}</span>
                                        <input
                                            type="text"
                                            className="small-input"
                                            {...register('address2')}
                                            disabled
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.streetFloor')}</span>
                                        <input
                                            type="text"
                                            className="small-input"
                                            {...register('address3')}
                                            disabled
                                        />
                                    </div>
                                </div>

                                <div className="form-section width-80">
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.abbreviationTranslation')}</span>
                                        <input
                                            type="text"
                                            className="small-input"
                                            {...register('nameTranslated', { required: true })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.titleTranslation')}</span>
                                        <input
                                            type="text"
                                            className="small-input"
                                            {...register('descriptionTranslated', { required: true })}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.addressTranslation')}</span>
                                        <input
                                            type="text"
                                            className="small-input"
                                            {...register('address1Translated')}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.address2Translation')}</span>
                                        <input
                                            type="text"
                                            className="small-input"
                                            {...register('address2Translated')}
                                        />
                                    </div>
                                    <div className="form-group">
                                        <span className="span-bold">{t('organizationalStructure.locations.form.address3Translation')}</span>
                                        <input
                                            type="text"
                                            className="small-input"
                                            {...register('address3Translated')}
                                        />
                                    </div>
                                </div>
                            </div>
                        </form>
                    </div>
                    <div className="modal-footer">
                        <button type="submit" form="translationsForm" className="btn btn-primary">
                            {t('common.save')}
                        </button>
                        <button type="button" className="btn btn-secondary" onClick={onClose}>
                            {t('common.cancel')}
                        </button>
                    </div>
                </div>
            </div>
        </div >
    );
}